import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, PRIMARY_OUTLET, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { LauncherModel } from '../models';
import { ContextStateService } from './context-state.service';

/**
 * Trennt Seitenname und Basis-Titel: "Dashboard · SITOS X". Der Seitenname steht
 * vorne, damit bei schmalen Browser-Tabs der unterscheidende Teil sichtbar bleibt.
 */
const TITLE_SEPARATOR = ' · ';

/**
 * Routen-Flag, mit dem eine Route ihren Seitennamen aus dem Sekundär-Menü
 * (Launcher-Text) bezieht: `data: { titleFromMenu: true }`.
 *
 * Deklaration an der Route statt einer Liste hier, damit die Pflege dort liegt,
 * wo man beim Anlegen einer Route ohnehin hinsieht -- gleiches Muster wie
 * `closeAdminNav` (siehe pages.module.ts und AdminNavigationStateService).
 *
 * Ohne Flag bleibt es beim reinen Basis-Titel. Das ist der Grund, warum die
 * Legacy-Frame-Seiten (`/{contextKey}/frame/{id}`) noch keinen Seitennamen
 * bekommen, obwohl sie je nach Mandant ebenfalls im Menü haengen: sie setzen
 * das Flag nicht.
 */
const MENU_TITLE_FLAG = 'titleFromMenu';

/** Schneidet Query-String und Fragment ab und normalisiert den Trailing-Slash. */
function toRoutePath(url: string): string {
  const path = url.split(/[?#]/)[0];
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Wertet `data.titleFromMenu` über die KOMPLETTE Routen-Kette (Root -> Blatt) aus.
 *
 * Bewusst nicht nur das Blatt: so laesst sich das Flag auch auf einer
 * Eltern-Route für einen ganzen Teilbaum setzen (z. B. eine Route mit
 * `loadChildren`), unabhängig von der paramsInheritanceStrategy. Der tiefste
 * gesetzte Wert gewinnt -- ein Kind kann das Flag der Eltern-Route also mit
 * `false` wieder abschalten. Gleiches Vorgehen wie
 * AdminNavigationStateService.#shouldCloseOnCurrentRoute().
 */
function wantsMenuTitle(snapshot: RouterStateSnapshot): boolean {
  let wanted = false;
  let route: ActivatedRouteSnapshot | undefined = snapshot.root;

  while (route) {
    const flag = route.data?.[MENU_TITLE_FLAG];
    if (typeof flag === 'boolean') {
      wanted = flag;
    }
    route = route.children.find((child) => child.outlet === PRIMARY_OUTLET);
  }

  return wanted;
}

/**
 * Sucht den Menü-Eintrag, dessen `path` auf `path` passt.
 *
 * Rekursiv, weil MenuHelper.createMenu() Einträge anhand von Backslashes im
 * Label zu Ordnern gruppiert -- verschachtelte Einträge haengen dann in `items`
 * und nicht auf der obersten Ebene.
 */
function findLauncherByPath(items: readonly LauncherModel[], path: string): LauncherModel | null {
  for (const item of items) {
    if (item.path && toRoutePath(item.path) === path) {
      return item;
    }
    const match = item.items?.length ? findLauncherByPath(item.items, path) : null;
    if (match) {
      return match;
    }
  }
  return null;
}

/**
 * Haelt den Browser-Titel (`<title>`) auf "<Seitenname> · <Basis-Titel>".
 *
 * Der Basis-Titel ist die Deployment-Konfiguration `window.__CONFIG.TITLE`; er
 * steht alleine, solange keine Seite einen Namen liefert. `index.html` liefert
 * ein leeres `<title>`, dieser Service ist also die einzige Quelle des Titels.
 *
 * Welche Route einen Seitennamen bekommt, entscheidet die Route selbst:
 *
 *   `title: 'Foo'`                 -> fester Titel (Angulars natives Route-Feld)
 *   `data: { titleFromMenu: true }` -> Name aus dem Sekundär-Menü
 *   nichts von beidem              -> nur der Basis-Titel
 *
 * Der Seitenname kommt NICHT aus den i18n-Dateien, sondern aus dem Launcher-Text
 * derselben Menü-Konfiguration, aus der die Hauptnavigation ihre Labels bezieht
 * (`AppContext.menuItems.secondary`, siehe NavigationMenuComponent). Damit sind
 * Tab-Titel und Menü-Beschriftung per Konstruktion identisch und es entsteht kein
 * zweiter Pflegeort. Konsequenz: bei einem Sprachwechsel bleibt der Titel -- wie
 * die Menü-Labels selbst -- auf dem beim Kontext-Load geholten Text stehen.
 *
 * Admin pages (e.g. /admin/statistics/dashboard) are not in the secondary menu; for them the
 * primary menu (admin navigation) is searched as well, see #menu.
 *
 * Registriert als `TitleStrategy` in AppModule; der Router ruft `updateTitle()`
 * nach jeder abgeschlossenen Navigation auf.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {
  readonly #title = inject(Title);
  readonly #contextState = inject(ContextStateService);
  readonly #destroyRef = inject(DestroyRef);

  /** Basis-Titel aus der Deployment-Config; Fallback wie bisher in der AppComponent. */
  readonly #baseTitle = window.__CONFIG?.TITLE || 'Frontend';

  /** Aktueller Routen-Pfad ohne Query/Fragment; null vor der ersten Navigation. */
  #path: string | null = null;

  /** Ob die aktive Route ihren Namen per `data.titleFromMenu` aus dem Menü zieht. */
  #menuTitle = false;

  /** Von der Route per `title:` deklarierter Titel; gewinnt über das Menü. */
  #routeTitle: string | null = null;

  /** Von `setDynamicTitle()` gesetzter Name; gewinnt über alles andere. */
  #dynamicName: string | null = null;

  /**
   * Menu entries of the active context that provide page names: secondary menu
   * first (redesign main navigation), then the primary menu (admin navigation,
   * e.g. /admin/statistics/dashboard). On a path present in both, the secondary entry wins.
   */
  #menu: readonly LauncherModel[] = [];

  constructor() {
    super();

    // Der Kontext wird vom ContextResolverGuard geladen und bei einem
    // Kontextwechsel erneut gesetzt. Ohne dieses Abo bliebe der Titel leer,
    // wenn der Kontext erst nach der Navigation eintrifft.
    this.#contextState.currentContext
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((context) => {
        this.#menu = [...(context?.menuItems?.secondary ?? []), ...(context?.menuItems?.primary ?? [])];
        this.#apply();
      });
  }

  /**
   * Setzt den Titel auf den aktuellen Stand. Wird von der AppComponent beim
   * Start aufgerufen, damit der Tab schon vor der ersten abgeschlossenen
   * Navigation den Basis-Titel zeigt.
   *
   * Idempotent: ist bereits ein Seitenname aufgelöst, bleibt er erhalten.
   */
  init(): void {
    this.#apply();
  }

  /** Router-Hook: laeuft nach jeder abgeschlossenen Navigation. */
  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.#path = toRoutePath(snapshot.url);
    this.#routeTitle = this.buildTitle(snapshot) ?? null;
    // Gemerkt statt bei jedem #apply() neu ausgewertet: der Titel wird auch
    // ausserhalb einer Navigation neu gesetzt (Kontext trifft ein / wechselt),
    // dann liegt kein Snapshot vor.
    this.#menuTitle = wantsMenuTitle(snapshot);
    // Ein dynamischer Name gilt nur für die Seite, die ihn gesetzt hat.
    this.#dynamicName = null;
    this.#apply();
  }

  /**
   * Setzt den Seitennamen der aktuellen Route explizit -- für Seiten, die ihren
   * Namen erst zur Laufzeit kennen und ihn nicht aus dem Menü beziehen können.
   *
   * Aktuell von keiner Seite genutzt. Vorgesehen für die Ablösung der
   * Legacy-Frame-Seiten: solange eine Seite ihren Namen aus geladenen Daten
   * bezieht (statt aus einem Launcher-Pfad), ist das der Weg dorthin.
   *
   * Gilt bis zur nächsten Navigation; `null` fällt auf den Menü-Lookup zurück.
   */
  setDynamicTitle(name: string | null): void {
    this.#dynamicName = name?.trim() || null;
    this.#apply();
  }

  #apply(): void {
    const name = this.#dynamicName ?? this.#routeTitle ?? this.#menuName();
    this.#title.setTitle(name ? `${name}${TITLE_SEPARATOR}${this.#baseTitle}` : this.#baseTitle);
  }

  /**
   * Seitenname aus dem Sekundär-Menü. Null, wenn die Route das Flag nicht
   * setzt, der Kontext noch nicht geladen ist oder es zum Pfad keinen Launcher
   * gibt -- dann bleibt der reine Basis-Titel stehen.
   */
  #menuName(): string | null {
    if (!this.#menuTitle || !this.#path) {
      return null;
    }
    return findLauncherByPath(this.#menu, this.#path)?.text?.trim() || null;
  }
}
